import React, { useEffect, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { cookingStrings, moduleCommonStrings } from '../../content/hobbyModules';
import { ratingStrings } from '../../content/ratings';
import { validateRecipeForm } from '../../validation/hobbies';
import EntryPhotoPicker from '../../components/EntryPhotoPicker';
import useStepperFlow from '../../hooks/useStepperFlow';
import { includesCollectionName, normalizeCollectionOptions, resolveCollectionMode } from '../../utils/collectionOptions';
import { showFirstStepWithErrors } from '../../utils/formStepErrors';

const emptyIngredient = { quantity: '', unit: '', item: '', note: '' };
const emptyStep = { text: '' };
const mealTypeOptions = ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Dessert', 'Meal Prep'];
const categoryOptions = ['Main', 'Side', 'Appetizer', 'Drink', 'Sauce', 'Baking', 'Soup', 'Salad', 'Dessert'];
const statusOptions = ['Want to try', 'Tried', 'Regular', 'Retired'];
const unitOptions = ['', 'tsp', 'tbsp', 'cup', 'fl oz', 'oz', 'lb', 'g', 'kg', 'ml', 'l', 'pinch', 'dash', 'clove', 'can', 'package', 'piece', 'slice', 'bunch', 'sprig', 'custom'];
const stepErrorFields = {
  recipe: ['title', 'description', 'sourceUrl', 'recipeBook'],
  ingredients: ['ingredients'],
  instructions: ['instructions'],
  organization: ['status', 'difficulty', 'categories', 'mealTypes', 'cuisine', 'dietary', 'tags', 'prepTime', 'cookTime', 'totalTime', 'servings', 'calories', 'protein'],
  nutrition: ['rating', 'notes'],
};

const emptyRecipe = {
  id: null,
  title: '',
  description: '',
  sourceUrl: '',
  recipeBook: cookingStrings.form.defaultRecipeBook,
  category: '',
  categories: [],
  mealTypes: [],
  status: 'Want to try',
  cuisine: '',
  dietary: '',
  tags: '',
  difficulty: 'Easy',
  prepTime: '',
  cookTime: '',
  totalTime: '',
  servings: '',
  rating: '',
  favorite: false,
  ingredients: [{ ...emptyIngredient }],
  instructions: [{ ...emptyStep }],
  notes: '',
  calories: '',
  protein: '',
  coverPhotoUrl: '',
  coverPhotoUpdatedAt: '',
};

/**
 * Normalizes ingredient text.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function parseIngredientText(value) {
  if (Array.isArray(value)) {
    return value.length ? value.map((ingredient) => (
      typeof ingredient === 'string'
        ? { ...emptyIngredient, item: ingredient }
        : { ...emptyIngredient, ...ingredient }
    )) : [{ ...emptyIngredient }];
  }
  return String(value || '')
    .split('\n')
    .filter(Boolean)
    .map((line) => ({ ...emptyIngredient, item: line.trim() }));
}

/**
 * Normalizes instruction text.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function parseInstructionText(value) {
  if (Array.isArray(value)) {
    return value.length ? value.map((step) => (
      typeof step === 'string' ? { text: step } : { ...emptyStep, ...step }
    )) : [{ ...emptyStep }];
  }
  return String(value || '')
    .split('\n')
    .map((line) => line.replace(/^\d+[\).]\s*/, '').trim())
    .filter(Boolean)
    .map((text) => ({ text }));
}

/**
 * Creates initial form.
 *
 * @param {*} initialData - initial data value.
 * @returns {*} The derived operation result.
 */
function createInitialForm(initialData) {
  if (!initialData) return emptyRecipe;

  return {
    ...emptyRecipe,
    ...initialData,
    dietary: Array.isArray(initialData.dietary) ? initialData.dietary.join(', ') : initialData.dietary || '',
    tags: Array.isArray(initialData.tags) ? initialData.tags.join(', ') : initialData.tags || '',
    categories: Array.isArray(initialData.categories) && initialData.categories.length
      ? initialData.categories
      : [initialData.category || emptyRecipe.category].filter(Boolean),
    mealTypes: Array.isArray(initialData.mealTypes) ? initialData.mealTypes : [],
    status: initialData.status || emptyRecipe.status,
    ingredients: parseIngredientText(initialData.ingredients),
    instructions: parseInstructionText(initialData.instructions),
  };
}

/**
 * Renders the field label experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.children - Nested content rendered by the component.
 * @param {*} props.required - required value.
 * @returns {JSX.Element} The rendered interface.
 */
function FieldLabel({ children, required = false }) {
  return (
    <label>
      <span>{children}</span>
      <em>{required ? cookingStrings.form.required : cookingStrings.form.optional}</em>
    </label>
  );
}

/**
 * Renders the star rating experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.value - Value to inspect or transform.
 * @param {*} props.onChange - Callback invoked when change occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function StarRating({ value, onChange }) {
  const rating = Number(value || 0);
  const stars = Array.from({ length: 5 }, (_, index) => index + 1);
  /**
   * Retrieves star state.
   *
   * @param {*} star - star value.
   * @returns {*} The derived operation result.
   */
  const getStarState = (star) => {
    if (rating >= star) return 'full';
    if (rating >= star - 0.5) return 'half';
    return 'empty';
  };

  return (
    <div className="star-rating-wrap">
      <div className="star-rating" role="radiogroup" aria-label={cookingStrings.form.fields.rating}>
        {stars.map((star) => (
          <span className={`star-button ${getStarState(star)}`} key={star}>
            <span className="star-icon" aria-hidden="true">★</span>
            <button
              type="button"
              className="star-hit star-hit-left"
              onClick={() => onChange(String(star - 0.5))}
              aria-pressed={rating === star - 0.5}
              aria-label={`${star - 0.5} stars`}
            />
            <button
              type="button"
              className="star-hit star-hit-right"
              onClick={() => onChange(String(star))}
              aria-pressed={rating === star}
              aria-label={`${star} stars`}
            />
          </span>
        ))}
      </div>
      <div className="star-rating-summary">
        <span>{rating ? ratingStrings.value(rating) : ratingStrings.notRated}</span>
        {rating > 0 && (
          <button type="button" className="btn-ghost compact-btn" onClick={() => onChange('')}>
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

/** Renders the step-based recipe create/edit flow and emits a normalized recipe draft. */
export default function CookingForm({ onSave, onCancel, isSaving, initialData, onDirtyChange, recipeBooks = [] }) {
  const initialForm = useMemo(() => createInitialForm(initialData), [initialData]);
  const [form, setForm] = useState(emptyRecipe);
  const [photoDraft, setPhotoDraft] = useState({ file: null, previewUrl: '', remove: false });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const steps = useMemo(() => ['recipe', 'ingredients', 'instructions', 'organization', 'nutrition'], []);
  const {
    activeSection,
    activeStepIndex,
    canGoBack,
    canGoNext,
    isStepSettled,
    goToSection,
    goToPreviousStep,
    goToNextStep,
    resetStepper,
  } = useStepperFlow(steps, 'recipe');
  const collectionOptions = useMemo(
    () => normalizeCollectionOptions(recipeBooks),
    [recipeBooks]
  );
  const isKnownCollection = includesCollectionName(collectionOptions, form.recipeBook);
  const [collectionMode, setCollectionMode] = useState('existing');

  useEffect(() => {
    setForm(initialForm);
    resetStepper('recipe');
    setPhotoDraft((current) => {
      if (current.previewUrl) URL.revokeObjectURL(current.previewUrl);
      return { file: null, previewUrl: '', remove: false };
    });
  }, [initialForm, resetStepper]);

  useEffect(() => {
    if (!collectionOptions.length) {
      setCollectionMode('new');
      return;
    }
    setCollectionMode(resolveCollectionMode({
      options: collectionOptions,
      value: initialForm.recipeBook,
      defaultValue: emptyRecipe.recipeBook,
    }));
  }, [collectionOptions, initialForm.recipeBook]);

  useEffect(() => {
    if (collectionMode === 'existing' && collectionOptions.length && !includesCollectionName(collectionOptions, form.recipeBook)) {
      updateField('recipeBook', collectionOptions[0]);
    }
  }, [collectionMode, collectionOptions, form.recipeBook]);

  useEffect(() => {
    onDirtyChange?.(JSON.stringify(form) !== JSON.stringify(initialForm) || Boolean(photoDraft.file) || photoDraft.remove);
  }, [form, initialForm, onDirtyChange, photoDraft.file, photoDraft.remove]);

  /**
   * Updates field.
   *
   * @param {string} field - Field associated with the operation.
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  /**
   * Performs meal type.
   *
   * @param {string} mealType - meal type value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const toggleMealType = (mealType) => {
    setForm((current) => {
      const currentMealTypes = Array.isArray(current.mealTypes) ? current.mealTypes : [];
      const mealTypes = currentMealTypes.includes(mealType)
        ? currentMealTypes.filter((item) => item !== mealType)
        : [...currentMealTypes, mealType];
      return { ...current, mealTypes };
    });
    setErrors((current) => ({ ...current, mealTypes: undefined }));
  };

  /**
   * Performs category.
   *
   * @param {*} category - category value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const toggleCategory = (category) => {
    setForm((current) => {
      const currentCategories = Array.isArray(current.categories) ? current.categories : [];
      const categories = currentCategories.includes(category)
        ? currentCategories.filter((item) => item !== category)
        : [...currentCategories, category];
      return { ...current, categories, category: categories[0] || '' };
    });
    setErrors((current) => ({ ...current, categories: undefined }));
  };

  /**
   * Updates ingredient.
   *
   * @param {number} index - index numeric value.
   * @param {string} field - Field associated with the operation.
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateIngredient = (index, field, value) => {
    setForm((current) => ({
      ...current,
      ingredients: current.ingredients.map((ingredient, ingredientIndex) => (
        ingredientIndex === index ? { ...ingredient, [field]: value } : ingredient
      )),
    }));
    if (field === 'item') setErrors((current) => ({ ...current, ingredients: undefined }));
  };

  /**
   * Updates step.
   *
   * @param {number} index - index numeric value.
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateStep = (index, value) => {
    setForm((current) => ({
      ...current,
      instructions: current.instructions.map((step, stepIndex) => (
        stepIndex === index ? { ...step, text: value } : step
      )),
    }));
    setErrors((current) => ({ ...current, instructions: undefined }));
  };

  /**
   * Creates ingredient.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const addIngredient = () => {
    setForm((current) => ({
      ...current,
      ingredients: [...current.ingredients, { ...emptyIngredient }],
    }));
  };

  /**
   * Removes ingredient.
   *
   * @param {number} index - index numeric value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const removeIngredient = (index) => {
    setForm((current) => ({
      ...current,
      ingredients: current.ingredients.length === 1
        ? [{ ...emptyIngredient }]
        : current.ingredients.filter((_, ingredientIndex) => ingredientIndex !== index),
    }));
  };

  /**
   * Creates step.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const addStep = () => {
    setForm((current) => ({
      ...current,
      instructions: [...current.instructions, { ...emptyStep }],
    }));
  };

  /**
   * Removes step.
   *
   * @param {number} index - index numeric value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const removeStep = (index) => {
    setForm((current) => ({
      ...current,
      instructions: current.instructions.length === 1
        ? [{ ...emptyStep }]
        : current.instructions.filter((_, stepIndex) => stepIndex !== index),
    }));
  };

  /**
   * Handles submit.
   *
   * @param {*} e - e value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (canGoNext) {
      goToNextStep();
      return;
    }
    if (!isStepSettled) return;
    if (collectionMode === 'new' && includesCollectionName(collectionOptions, form.recipeBook)) {
      const nextErrors = { recipeBook: cookingStrings.form.errors.recipeBookDuplicate };
      setErrors((current) => ({ ...current, ...nextErrors }));
      showFirstStepWithErrors(nextErrors, stepErrorFields, 'recipe', goToSection);
      return;
    }
    const validation = validateRecipeForm(form);
    setErrors(validation.fieldErrors);
    if (!validation.valid) {
      showFirstStepWithErrors(validation.fieldErrors, stepErrorFields, 'recipe', goToSection);
      return;
    }
    setIsSubmitting(true);
    try {
      await onSave({
        ...form,
        coverPhotoFile: photoDraft.file,
        shouldRemoveCoverPhoto: photoDraft.remove,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="recipe-form" onSubmit={(event) => event.preventDefault()} noValidate>
      <div className="hobby-flow-stepper" role="tablist" aria-label="Recipe editor steps">
        {steps.map((section, index) => (
          <button
            key={section}
            type="button"
            className={activeSection === section ? 'active' : index < activeStepIndex ? 'complete' : ''}
            onClick={() => goToSection(section)}
            role="tab"
            aria-selected={activeSection === section}
          >
            <span>{index + 1}</span>
            {cookingStrings.form.sections[section]}
          </button>
        ))}
      </div>

      {activeSection === 'recipe' && (
      <section className="recipe-form-section priority-section">
        <h3>{cookingStrings.form.sections.recipe}</h3>
        <div className="form-grid two-columns">
          <div className="form-group">
            <FieldLabel required>{cookingStrings.form.fields.recipeName}</FieldLabel>
            <input
              type="text"
              aria-invalid={Boolean(errors.title)}
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              placeholder={cookingStrings.form.placeholders.recipeName}
            />
            {errors.title && <p className="field-error">{errors.title}</p>}
          </div>

          <div className="form-group collection-field">
            <FieldLabel required>{cookingStrings.form.fields.recipeBook}</FieldLabel>
            <div className="collection-picker" aria-invalid={Boolean(errors.recipeBook)}>
              {collectionOptions.length > 0 && (
                <div className="collection-mode-toggle" role="group" aria-label="Recipe book mode">
                  <button
                    type="button"
                    className={collectionMode === 'existing' ? 'active' : ''}
                    onClick={() => {
                      setCollectionMode('existing');
                      updateField('recipeBook', isKnownCollection ? form.recipeBook : collectionOptions[0]);
                    }}
                  >
                    {cookingStrings.form.actions.useExisting}
                  </button>
                  <button
                    type="button"
                    className={collectionMode === 'new' ? 'active' : ''}
                    onClick={() => {
                      setCollectionMode('new');
                      if (isKnownCollection) updateField('recipeBook', '');
                    }}
                  >
                    {cookingStrings.form.actions.createNew}
                  </button>
                </div>
              )}
              {collectionMode === 'existing' && collectionOptions.length > 0 ? (
                <select value={isKnownCollection ? form.recipeBook : collectionOptions[0]} onChange={(e) => updateField('recipeBook', e.target.value)}>
                  {collectionOptions.map((book) => <option key={book} value={book}>{book}</option>)}
                </select>
              ) : (
                <input
                  value={form.recipeBook}
                  onChange={(e) => updateField('recipeBook', e.target.value)}
                  placeholder={cookingStrings.form.defaultRecipeBook}
                />
              )}
            </div>
            {errors.recipeBook && <p className="field-error">{errors.recipeBook}</p>}
          </div>

          <div className="form-group form-grid-full">
            <FieldLabel>{cookingStrings.form.fields.description}</FieldLabel>
            <textarea
              rows="3"
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder={cookingStrings.form.placeholders.description}
            />
          </div>

          <div className="form-group form-grid-full">
            <button
              type="button"
              className={`favorite-toggle inline-favorite-toggle ${form.favorite ? 'active' : ''}`}
              onClick={() => updateField('favorite', !form.favorite)}
              aria-pressed={form.favorite}
            >
              <span>{form.favorite ? cookingStrings.form.favorite : cookingStrings.form.favoriteAction}</span>
            </button>
          </div>
        </div>

        {initialData?.sourceUrl && (
          <div className="import-source-note">
            {cookingStrings.form.importedFrom} <span>{initialData.sourceUrl}</span>
          </div>
        )}
      </section>
      )}

      {activeSection === 'ingredients' && (
      <section className="recipe-form-section">
        <h3>{cookingStrings.form.fields.ingredients}</h3>
        <p className="muted-copy">{cookingStrings.form.ingredientHelp}</p>

        <div className="ingredient-editor">
          {form.ingredients.map((ingredient, index) => (
            <div className="ingredient-row" key={`ingredient-${index}`}>
              <div className="cooking-row-heading">
                <span>{cookingStrings.form.actions.ingredientRow(index + 1)}</span>
                <button type="button" className="cooking-row-delete" onClick={() => removeIngredient(index)}
                  aria-label={cookingStrings.form.actions.removeIngredient(index + 1)}
                  title={cookingStrings.form.actions.removeIngredient(index + 1)}>
                  <Trash2 size={17} aria-hidden="true" />
                </button>
              </div>
              <div className="ingredient-row-fields">
                <input
                  value={ingredient.item}
                  onChange={(e) => {
                    updateIngredient(index, 'item', e.target.value);
                    setErrors((current) => ({ ...current, ingredients: undefined }));
                  }}
                  placeholder={cookingStrings.form.placeholders.ingredient}
                  aria-invalid={Boolean(errors.ingredients)}
                />
                <input
                  value={ingredient.quantity}
                  onChange={(e) => updateIngredient(index, 'quantity', e.target.value)}
                  placeholder={cookingStrings.form.placeholders.quantity}
                  inputMode="decimal"
                  aria-invalid={Boolean(errors[`ingredients.${index}.quantity`])}
                />
                <select
                  value={ingredient.unit}
                  onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                >
                  <option value="">{cookingStrings.form.placeholders.unit}</option>
                  {unitOptions.filter(Boolean).map((unit) => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
        <button type="button" className="btn-secondary cooking-add-row" onClick={addIngredient}>{cookingStrings.form.actions.addIngredient}</button>
        {errors.ingredients && <p className="field-error">{errors.ingredients}</p>}
        {Object.entries(errors).filter(([field]) => field.startsWith('ingredients.')).map(([field, message]) => (
          <p className="field-error" key={field}>{message}</p>
        ))}
      </section>
      )}

      {activeSection === 'instructions' && (
      <section className="recipe-form-section">
        <h3>{cookingStrings.form.fields.instructions}</h3>
        <p className="muted-copy">{cookingStrings.form.instructionHelp}</p>

        <div className="instruction-editor">
          {form.instructions.map((step, index) => (
            <div className="instruction-row" key={`step-${index}`}>
              <div className="cooking-row-heading">
                <span>{cookingStrings.form.actions.stepRow(index + 1)}</span>
                <button type="button" className="cooking-row-delete" onClick={() => removeStep(index)}
                  aria-label={cookingStrings.form.actions.removeStep(index + 1)}
                  title={cookingStrings.form.actions.removeStep(index + 1)}>
                  <Trash2 size={17} aria-hidden="true" />
                </button>
              </div>
              <textarea
                rows="2"
                value={step.text}
                onChange={(e) => {
                  updateStep(index, e.target.value);
                  setErrors((current) => ({ ...current, instructions: undefined }));
                }}
                placeholder={cookingStrings.form.placeholders.step}
                aria-invalid={Boolean(errors.instructions)}
              />
            </div>
          ))}
        </div>
        <button type="button" className="btn-secondary cooking-add-row" onClick={addStep}>{cookingStrings.form.actions.addStep}</button>
        {errors.instructions && <p className="field-error">{errors.instructions}</p>}
      </section>
      )}

      {activeSection === 'organization' && (
      <>
      <section className="recipe-form-section">
        <h3>{cookingStrings.form.sections.organization}</h3>
        <div className="form-subsection-stack">
          <div className="form-group">
            <FieldLabel>{cookingStrings.form.fields.status}</FieldLabel>
            <select value={form.status} onChange={(e) => updateField('status', e.target.value)}>
              {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
          </div>

          <div className="form-group">
            <FieldLabel>{cookingStrings.form.fields.mealTypes}</FieldLabel>
            <div className="chip-toggle-group">
              {mealTypeOptions.map((mealType) => (
                <button
                  type="button"
                  key={mealType}
                  className={form.mealTypes?.includes(mealType) ? 'active' : ''}
                  onClick={() => toggleMealType(mealType)}
                >
                  {mealType}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <FieldLabel>{cookingStrings.form.fields.category}</FieldLabel>
            <div className="chip-toggle-group">
              {categoryOptions.map((category) => (
                <button
                  type="button"
                  key={category}
                  className={form.categories?.includes(category) ? 'active' : ''}
                  onClick={() => toggleCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div className="form-grid three-columns">
            <div className="form-group">
              <FieldLabel>{cookingStrings.form.fields.cuisine}</FieldLabel>
              <input
                value={form.cuisine}
                onChange={(e) => updateField('cuisine', e.target.value)}
                placeholder={cookingStrings.form.placeholders.cuisine}
              />
            </div>

            <div className="form-group">
              <FieldLabel>{cookingStrings.form.fields.dietary}</FieldLabel>
              <input
                value={form.dietary}
                onChange={(e) => updateField('dietary', e.target.value)}
                placeholder={cookingStrings.form.placeholders.dietary}
              />
            </div>

            <div className="form-group">
              <FieldLabel>{cookingStrings.form.fields.tags}</FieldLabel>
              <input
                value={form.tags}
                onChange={(e) => updateField('tags', e.target.value)}
                placeholder={cookingStrings.form.placeholders.tags}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="recipe-form-section">
        <h3>{cookingStrings.form.sections.timing}</h3>
        <div className="form-grid five-columns">
          <div className="form-group">
            <FieldLabel>{cookingStrings.form.fields.prep}</FieldLabel>
            <input type="number" min="0" inputMode="numeric" value={form.prepTime} onChange={(e) => updateField('prepTime', e.target.value)} placeholder={cookingStrings.form.placeholders.minutes} aria-invalid={Boolean(errors.prepTime)} />
            {errors.prepTime && <p className="field-error">{errors.prepTime}</p>}
          </div>
          <div className="form-group">
            <FieldLabel>{cookingStrings.form.fields.cook}</FieldLabel>
            <input type="number" min="0" inputMode="numeric" value={form.cookTime} onChange={(e) => updateField('cookTime', e.target.value)} placeholder={cookingStrings.form.placeholders.minutes} aria-invalid={Boolean(errors.cookTime)} />
            {errors.cookTime && <p className="field-error">{errors.cookTime}</p>}
          </div>
          <div className="form-group">
            <FieldLabel>{cookingStrings.form.fields.total}</FieldLabel>
            <input type="number" min="0" inputMode="numeric" value={form.totalTime} onChange={(e) => updateField('totalTime', e.target.value)} placeholder={cookingStrings.form.placeholders.minutes} aria-invalid={Boolean(errors.totalTime)} />
            {errors.totalTime && <p className="field-error">{errors.totalTime}</p>}
          </div>
          <div className="form-group">
            <FieldLabel>{cookingStrings.form.fields.servings}</FieldLabel>
            <input
              type="number"
              min="0"
              inputMode="numeric"
              value={form.servings}
              onChange={(e) => updateField('servings', e.target.value)}
              placeholder={cookingStrings.form.placeholders.servings}
              aria-invalid={Boolean(errors.servings)}
            />
            {errors.servings && <p className="field-error">{errors.servings}</p>}
          </div>
          <div className="form-group">
            <FieldLabel>{cookingStrings.form.fields.difficulty}</FieldLabel>
            <select value={form.difficulty} onChange={(e) => updateField('difficulty', e.target.value)}>
              <option>Easy</option>
              <option>Medium</option>
              <option>Advanced</option>
            </select>
          </div>
          {form.sourceUrl && (
            <div className="form-group form-grid-full">
              <FieldLabel>{cookingStrings.form.fields.sourceUrl}</FieldLabel>
              <input value={form.sourceUrl} readOnly aria-invalid={Boolean(errors.sourceUrl)} />
              {errors.sourceUrl && <p className="field-error">{errors.sourceUrl}</p>}
            </div>
          )}
          <div className="form-group">
            <FieldLabel>{cookingStrings.form.fields.calories}</FieldLabel>
            <input value={form.calories} onChange={(e) => updateField('calories', e.target.value)} placeholder={cookingStrings.form.placeholders.perServing} />
          </div>
          <div className="form-group">
            <FieldLabel>{cookingStrings.form.fields.protein}</FieldLabel>
            <input value={form.protein} onChange={(e) => updateField('protein', e.target.value)} placeholder={cookingStrings.form.placeholders.perServing} />
          </div>
        </div>
      </section>
      </>
      )}

      {activeSection === 'nutrition' && (
      <>
      <section className="recipe-form-section">
        <EntryPhotoPicker
          photoUrl={form.coverPhotoUrl}
          draft={photoDraft}
          onChange={setPhotoDraft}
          title={cookingStrings.form.fields.photos}
        />
      </section>
      <section className="recipe-form-section">
        <h3>{cookingStrings.form.sections.nutrition}</h3>
        <div className="form-grid two-columns">
          <div className="form-group">
            <FieldLabel>{cookingStrings.form.fields.rating}</FieldLabel>
            <StarRating value={form.rating} onChange={(rating) => updateField('rating', rating)} />
            {errors.rating && <p className="field-error">{errors.rating}</p>}
          </div>
          <div className="form-group form-grid-full">
            <FieldLabel>{cookingStrings.form.fields.notes}</FieldLabel>
            <textarea
              rows="4"
              value={form.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder={cookingStrings.form.placeholders.notes}
            />
          </div>
        </div>
      </section>
      </>
      )}

      <div className="form-actions sticky-actions cooking-stepper-actions">
        <button type="button" className="btn-ghost" onClick={() => onCancel({ confirmDiscard: true })}>{moduleCommonStrings.actions.cancel}</button>
        <div className="stepper-actions">
          {canGoBack && (
            <button type="button" className="btn-secondary" onClick={goToPreviousStep}>
              {moduleCommonStrings.actions.back}
            </button>
          )}
          {canGoNext ? (
            <button type="button" className="btn-primary" onClick={goToNextStep}>
              {moduleCommonStrings.actions.next}
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={isSaving || isSubmitting || !isStepSettled}
            >
              {isSaving || isSubmitting ? moduleCommonStrings.actions.saving : cookingStrings.form.save}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
